/**
 * The compute gate's refusals, as a sentence the person can act on (ENG-3091).
 *
 * `gate_compute_submission` refuses a Diagnosis, MeshGen or SimOps submission
 * with a 403 whose body names the product but carries no prose:
 *
 *   - `{ error: 'seat_not_assigned', product }` — the organization holds the
 *     product but this member has no seat in it (ENG-2297, per product since
 *     ENG-2337). The fix is the admin's: assign a seat.
 *   - `{ error: 'product_not_entitled', product }` — the organization holds no
 *     grant for the product at all. The fix is a subscription.
 *
 * Without this the body reaches `apiErrorMessage` with no `message`/`detail`,
 * which returns `''`, and every submit surface falls back to its generic
 * "Failed to …" string — the refusal reads as our failure rather than the
 * reader's state. The two codes stay distinct because the remedies differ; see
 * `_handle_seat_not_assigned` in `backend/src/job/api_handler.py`.
 *
 * Khorium Agent refuses over AG-UI instead (`RUN_ERROR.code === 'not-entitled'`,
 * mapped in `agui-agent.ts`), so it never reaches here.
 */
import i18n from '@/i18n';
import { billingProductLabel } from '@/config/usageProducts';

export type EntitlementDenial = {
  reason: 'seat' | 'subscription';
  /** The backend's product key, e.g. `meshgen`. */
  product: string;
};

const REASON_BY_CODE: Readonly<Record<string, EntitlementDenial['reason']>> = {
  seat_not_assigned: 'seat',
  product_not_entitled: 'subscription',
};

/** The refusal in a rejected API call's body, or `null` for anything else. */
export function parseEntitlementDenial(
  error: unknown,
): EntitlementDenial | null {
  if (typeof error !== 'object' || error === null) return null;
  const { error: code, product } = error as {
    error?: unknown;
    product?: unknown;
  };
  if (typeof code !== 'string' || !Object.hasOwn(REASON_BY_CODE, code)) {
    return null;
  }
  return {
    reason: REASON_BY_CODE[code]!,
    product: typeof product === 'string' && product ? product : '',
  };
}

/**
 * The translated sentence for a gate refusal, or `''` when `error` is not one,
 * so callers can chain it with `||` like `apiErrorMessage`.
 */
export function entitlementDenialMessage(error: unknown): string {
  const denial = parseEntitlementDenial(error);
  return denial ? entitlementDenialText(denial) : '';
}

/**
 * The translated sentence for an already-parsed refusal. Resolved through
 * `i18n.t` on every call, never cached, so it follows the active locale.
 */
export function entitlementDenialText(denial: EntitlementDenial): string {
  // A body with no product is a backend bug; still say what is wrong rather
  // than dropping to the generic failure.
  const product = denial.product
    ? billingProductLabel(denial.product)
    : i18n.t('compCommon.entitlement.thisProduct');
  return denial.reason === 'seat'
    ? i18n.t('compCommon.entitlement.noSeat', { product })
    : i18n.t('compCommon.entitlement.noSubscription', { product });
}
